import React from "react";
import ReactDOM from "react-dom";
import Moment from "moment";
import { createBrowserHistory } from "history";
import { version as rhombusVersion } from "rhombus-react/package.json";

import config, { appVersion } from "utils/appConfig";

import makeStore from './store/store';
import App from "./App";
import Notification from "./Notification";
import { USER_ACTIONS } from "reducers/You";

import { LS_REDIRECT, USER_LOCALE } from "utils/constants";

import { errorLog } from "utils/Rave";
import initGrafana from "utils/Grafana";

import { setKey } from "utils/Keyboard";
import {
	clearFlags,
	enablePRVW,
	disablePRVW,
	FLAG_POLE,
	PRVW_1_KEY,
	PRVW_2_KEY,
	getVersionGenSelectedPreview,
	ZENDESK_HELP_KEY,
	toggleZendeskHelp
} from "utils/Flags";

import "./css/Vendor.css";
import "./index.css";
import "./global.css";

var routerHistory = createBrowserHistory()
	, redirectBack = localStorage.getItem(LS_REDIRECT)
	;

// The first thing we do should be check for a redirect
// if it exists, parse it and if it's still valid, then send the user to that URL
if(redirectBack) {
	let parsed = JSON.parse(redirectBack)
		, now = Date.now()
		, fiveMinutes = 300000
		;

	if((now - parsed.ttl) < fiveMinutes) {
		localStorage.removeItem(LS_REDIRECT);
		window.location.href = parsed.url;
	}
}

function initialRender(){
	let userLang = USER_LOCALE;

	// In order for locales to work, we need moment to exist in the global scope
	window.moment = Moment;
	if(!/en-us/gi.test(userLang)){
		try {
			let localeScript = document.createElement("script");

			localeScript.addEventListener("load", () => {
				Moment.locale(userLang);
				_renderApp();
			});
			localeScript.addEventListener("error", (err) => {
				errorLog({ error:`Could not load the locale: ${userLang}` });
				_renderApp();
			});
			localeScript.setAttribute("charset", "UTF-8");
			localeScript.setAttribute("src", `/locales/dates/${userLang}.js`);
			document.head.appendChild(localeScript);
		} catch (err){
			errorLog({ error:`Could not set the locale: ${userLang}` });
			_renderApp();
		}
		return;
	}

	_renderApp();
}

/**
 * [_initSentry description]
 * 		Starts up Sentry error logging
 * 			Only start it up if we are not in Develop mode
 * @return {null}
 */
function _initSentry(){
	// Disable Sentry for local testing
	if(/dev/gi.test(config.buildENV)){
		console.info("%cSentry%c is %cdisabled%c for local testing", "color: #1a42bf; font-weight:bold;", "color:#fff; font-weight:normal;", "color:red; font-weight:bold;", "color:#fff; font-weight:normal;");
		console.log();
		return;
	}

	if(!config.sentryDSN){
		console.info("%cSentry startup error!%c", "color: #1a42bf; font-weight:bold; background-color:#0ba8e6; padding:2px", "color:default; font-weight:normal;");
		return;
	}

	try {
	/* eslint-disable no-undef */
		Sentry.init({
			dsn: config.sentryDSN,
			release:appVersion,
			environment:config.buildENV,
		});
	/* eslint-enable no-undef */
	} catch (err) {
		console.groupCollapsed("Sentry Startup issue:");
		console.error(err);
		console.groupEnd();
	}
}

function _renderApp(){
	_initSentry();
	// Remove all enabled Feature Flags on startup
	clearFlags(FLAG_POLE);

	// Set PRVW2
	setKey(PRVW_2_KEY, enablePRVW);
	setKey(PRVW_1_KEY, disablePRVW);

	setKey(ZENDESK_HELP_KEY, toggleZendeskHelp);

	if(getVersionGenSelectedPreview()){
		console.info("%cAI%c Dev Mode %cEnabled!%c \n\nEnjoy the ride\n\n🕶️🕶️🕶️🕶️🕶️🕶️🕶️🕶️🕶️🕶️",
			"color: #cd0184; font-weight:bold;",
			"color:#000;font-weight:normal; text-decporation:underline;",
			"color:#19b540; font-weight:bold;",
			"color:#000;font-weight:normal;"
		);
	}

	let userAgent = navigator.userAgent.toLocaleLowerCase()
		, dbody = document.body
		, browser = "";

	if(userAgent.indexOf("chrome") > -1){
		browser = "Chrome";
		dbody.classList.add("is-chrome");
	} else if(userAgent.indexOf("firefox") > -1){
		browser = "FireFox";
		dbody.classList.add("is-ff");
	} else if(userAgent.indexOf("safari") > -1){
		browser = "Safari";
		dbody.classList.add("is-sf");
	}

	document.body.classList.add(`env-${process.env.NODE_ENV.toLowerCase()}`, `is-${config.buildENV}`);

	//Set up Grafana Tracing
	initGrafana(routerHistory);

	render(App)
		.then((store) => {
			try {
				store.dispatch({
					type: USER_ACTIONS.SET_USER_INFO,
					payload:{
						browser,
						locale:navigator.language,
					},
				});
			} catch (err){
				console.warn("There was a problem obtaining browser settings");
			}
		});

	// Some basic application information for use verifying issues in PROD
	window.__INFO = {
		buildDate: config.buildTime,
		buildNumber:config.buildNumber,
		commitHash: config.commitHash,
		rhombusVersion,
	};
}

function render(Component){
	var store = makeStore({ }, routerHistory)
		, PP = new Promise((resolve, reject) => {
			ReactDOM.render(
				<Component store={store} history={routerHistory} />,
				document.getElementById("hopi-root"),
				() => resolve(store)
			);
		});

	// In order to have the Notifications always appear on top, we render this to it's own container
	ReactDOM.render(
		<Notification store = {store} />,
		document.getElementById("notification-container")
	);

	return PP;
}

initialRender();
